import { Check as CheckIcon } from '@mui/icons-material';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useUploaderContext } from '@/context/UploaderProvider';

const STEP_LABELS: Record<number, string> = {
  1: 'Reading report...',
  2: 'Extracting text...',
  3: 'Analyzing with AI...',
  4: 'Building dashboard...',
  5: 'Complete',
};

const STEP_PROGRESS: Record<number, number> = {
  1: 15,
  2: 40,
  3: 65,
  4: 90,
  5: 100,
};

export const SidebarProcessingIndicator = () => {
  const { stepProcessing, streamingProperty, resetProcessing, lastProcessedPropertyId } =
    useUploaderContext();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);

  const isActive = stepProcessing >= 1 && stepProcessing <= 5;

  useEffect(() => {
    if (isActive) {
      setVisible(true);
    }
  }, [isActive]);

  // Auto-hide after completion
  useEffect(() => {
    if (stepProcessing === 5) {
      const timer = setTimeout(() => {
        setVisible(false);
        resetProcessing();
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [stepProcessing]);

  const handleClick = () => {
    if (stepProcessing === 5 && lastProcessedPropertyId) {
      setVisible(false);
      resetProcessing();
      navigate('/dashboard/summary');
    }
  };

  if (!visible || !isActive) return null;

  const isComplete = stepProcessing === 5;
  const progress = STEP_PROGRESS[stepProcessing] ?? 0;

  return (
    <Box
      onClick={isComplete ? handleClick : undefined}
      sx={{
        px: 0,
        pb: '14px',
        cursor: isComplete ? 'pointer' : 'default',
      }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
        }}>
        {/* Progress spinner or check */}
        {isComplete ? (
          <CheckIcon sx={{ fontSize: 16, color: '#5a8a00', flexShrink: 0 }} />
        ) : (
          <Box sx={{ position: 'relative', width: 18, height: 18, flexShrink: 0 }}>
            {/* Background track */}
            <CircularProgress
              variant="determinate"
              value={100}
              size={18}
              thickness={4}
              sx={{
                color: 'rgba(192,255,2,0.15)',
                position: 'absolute',
                top: 0,
                left: 0,
              }}
            />
            {/* Progress fill */}
            <CircularProgress
              variant="determinate"
              value={progress}
              size={18}
              thickness={4}
              sx={{
                color: 'var(--tx-lime-ink)',
                position: 'absolute',
                top: 0,
                left: 0,
                transition: 'all 0.8s ease-in-out',
                animation: 'spinRamp 1.4s ease-in-out infinite',
                '@keyframes spinRamp': {
                  '0%': { transform: 'rotate(0deg)' },
                  '50%': { transform: 'rotate(270deg)' },
                  '100%': { transform: 'rotate(720deg)' },
                },
              }}
            />
          </Box>
        )}

        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Property address */}
          {streamingProperty && (
            <Typography
              variant="caption"
              noWrap
              sx={{
                display: 'block',
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--tx-muted)',
                lineHeight: 1.1,
                mb: 0,
              }}>
              {streamingProperty}
            </Typography>
          )}

          {/* Step label */}
          <Typography
            variant="caption"
            sx={{
              fontSize: 10,
              color: isComplete ? '#5a8a00' : 'var(--tx-muted)',
              lineHeight: 1.2,
            }}>
            {STEP_LABELS[stepProcessing] ?? 'Processing...'}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
